import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MapPinIcon, BriefcaseIcon, SearchIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
import { getMemberPhotoStyle, formatMemberLocation, getTranslatedField } from '../utils/member-helpers';
export function MembersDirectoryPage() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [search, setSearch] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredMembers = MEMBER_PROFILES.filter(member => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return member.name.toLowerCase().includes(q) || member.city.toLowerCase().includes(q) || member.state.toLowerCase().includes(q) || member.industry.toLowerCase().includes(q) || (member.industryEs || '').toLowerCase().includes(q) || member.role.toLowerCase().includes(q) || (member.roleEs || '').toLowerCase().includes(q) || (member.company || '').toLowerCase().includes(q) || member.badge.toLowerCase().includes(q) || (member.badgeEs || '').toLowerCase().includes(q);
  });
  return <div className="min-h-screen bg-haa-navy text-white font-nunito" data-id="element-3801" data-mp="3phco9r8837v5">
      {/* Header */}
      <header className="w-full px-6 py-6 sm:px-12 flex items-center justify-between border-b border-white/10" data-id="element-3802" data-mp="31r8ibd7kaw0z">
        <a href="#" className="flex items-center gap-3 group" data-id="element-3803" data-mp="2wtyv847nawgb">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-3804" data-mp="3asgftt297su0" />
        </a>
        <a href="#" className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-sm transition-colors" data-id="element-3805" data-mp="m15r83kbc8vc">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3806" data-mp="3exqixiyz1cpo" />
          {isEs ? 'Volver al Inicio' : 'Back to Home'}
        </a>
      </header>

      {/* Hero Banner with Flags */}
      <div className="relative overflow-hidden" data-id="element-3807" data-mp="3ouv5hjwqba70">
        <div className="absolute inset-0" data-id="element-3808" data-mp="3suvm9b7avxh8">
          <img src="https://cdn.magicpatterns.com/uploads/2NpszK87251xAn5sA6iUcH/iStock-1096894272.jpg" alt="" className="w-full h-full object-cover" data-id="element-3809" data-mp="3stucda20th13" />
          <div className="absolute inset-0 bg-haa-navy/80" data-id="element-3810" data-mp="314r7bs2b82ne" />
          <div className="absolute inset-0 bg-gradient-to-b from-haa-navy/30 via-transparent to-haa-navy" data-id="element-3811" data-mp="355t6k8w88epd" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 sm:px-12 py-16 sm:py-24 text-center" data-id="element-3812" data-mp="39wjffw3v2zot">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3813" data-mp="2df6jovv7oh7s">
            <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3814" data-mp="24yfn7hy49grt">
              {isEs ? 'Nuestra Gente, Nuestras Historias' : 'Our People, Our Stories'}
            </span>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-white tracking-wider mt-3 leading-none drop-shadow-lg" data-id="element-3815" data-mp="1vz0xwltorc8w">
              {isEs ? 'Conoce a Nuestra Gente' : 'Meet Our People'}
            </h1>
            <p className="font-nunito text-white/70 text-lg mt-4 max-w-2xl mx-auto" data-id="element-3816" data-mp="1h7ind3jli9wx">
              {isEs ? 'Hondureño-americanos generando un impacto en todo Estados Unidos, liderando en los negocios, el servicio público y la comunidad.' : 'Honduran-Americans making an impact across the United States, leading in business, public service, and community.'}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12 -mt-6 relative z-10" data-id="element-3817" data-mp="33cml29g84hc6">
        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-1.5" data-id="element-3818" data-mp="388k1sgvefpph">
          <div className="relative" data-id="element-3819" data-mp="2tjxkwaq4ipw5">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" data-id="element-3820" data-mp="2yjjxqj21i44f" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={isEs ? 'Buscar por nombre, ciudad, estado, industria...' : 'Search by name, city, state, industry...'} className="w-full bg-transparent text-white placeholder-white/40 font-nunito text-base pl-12 pr-12 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-haa-skyblue/50" data-id="element-3821" data-mp="2kk0t408ftawv" />
            {search && <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 transition-colors" aria-label="Clear search" data-id="element-3822" data-mp="dsjjsjiu5yqy">
                <XIcon className="w-4 h-4 text-white/50" data-id="element-3823" data-mp="281ootfdo0mdk" />
              </button>}
          </div>
        </div>
        {search.trim() && <p className="font-nunito text-white/50 text-sm mt-3 ml-1" data-id="element-3824" data-mp="2395dkp2ga3bx">
            {filteredMembers.length}{' '}
            {isEs ? filteredMembers.length === 1 ? 'resultado' : 'resultados' : filteredMembers.length === 1 ? 'result' : 'results'}{' '}
            {isEs ? 'para' : 'for'} "{search}"
          </p>}
      </div>

      {/* Members Grid */}
      <main className="max-w-6xl mx-auto px-6 sm:px-12 py-12 lg:py-16" data-id="element-3825" data-mp="wlhxf43li2tr">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3826" data-mp="rmwv8z4s992o">
          {filteredMembers.map((member, index) => {
          const role = getTranslatedField(member, 'role', isEs);
          const industry = getTranslatedField(member, 'industry', isEs);
          const badge = getTranslatedField(member, 'badge', isEs);
          return <motion.a key={member.slug} href={`#/member/${member.slug}`} initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: Math.min(index * 0.1, 0.5),
            ease: [0.16, 1, 0.3, 1]
          }} className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300" data-id="element-3827" data-mp="1qa5wz9hdxkz6" data-mpi={index}>
                {/* Photo — only the name overlays the image, anchored bottom-left */}
                <div className="relative aspect-[4/3] overflow-hidden" data-id="element-3828" data-mp="ccowu735zljk">
                  <img src={member.photo} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={getMemberPhotoStyle(member.slug, 'card')} data-id="element-3829" data-mp="c9t0uoavn6xr" />
                  {/* Subtle gradient only at the very bottom so the face stays clear */}
                  <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-haa-navy/85 to-transparent" data-id="element-3830" data-mp="v4hqm3q25asc" />

                  {/* Badge */}
                  {badge && <div className="absolute top-4 right-4" data-id="element-3831" data-mp="lb0z56de87ru">
                      <span className="inline-block px-3 py-1 bg-haa-skyblue text-white font-bebas text-sm tracking-wider rounded-full shadow-sm" data-id="element-3832" data-mp="geis1zulbxcc">
                        {badge}
                      </span>
                    </div>}

                  {/* Name overlay — bottom left, name only */}
                  <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-3833" data-mp="he1ygn6td4tu">
                    <h2 className="font-bebas text-3xl text-white tracking-wider leading-none drop-shadow-lg" data-id="element-3834" data-mp="m5tomwm258jg">
                      {member.name}
                    </h2>
                  </div>
                </div>

                {/* Info panel — role, company, industry and location live here */}
                <div className="p-5" data-id="element-3835" data-mp="112kt3kal9afx">
                  <p className="font-nunito text-haa-skyblue font-semibold text-sm leading-snug" data-id="element-3836" data-mp="113m1ti44tkuh">
                    {role}
                  </p>
                  {member.company && <p className="font-nunito text-white/70 text-sm mt-0.5" data-id="element-3837" data-mp="r44yv8g9ll27">
                      {member.company}
                    </p>}

                  <div className="flex flex-col gap-2 mt-3 mb-4" data-id="element-3838" data-mp="3bucgl0y4yjbs">
                    <div className="flex items-center gap-2 text-white/60 text-sm" data-id="element-3839" data-mp="3bx8h8x1dbm3x">
                      <MapPinIcon className="w-3.5 h-3.5 text-haa-skyblue flex-shrink-0" data-id="element-3840" data-mp="3gv9vn4numx92" />
                      <span data-id="element-3841" data-mp="398mlg7mbuxeo">
                        {formatMemberLocation(member.city, member.state)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60 text-sm" data-id="element-3842" data-mp="3ibnou0jt6aen">
                      <BriefcaseIcon className="w-3.5 h-3.5 text-haa-skyblue flex-shrink-0" data-id="element-3843" data-mp="3dcavcwyfm533" />
                      <span data-id="element-3844" data-mp="2pmgkum1l4sm0">{industry}</span>
                    </div>
                  </div>

                  <div className="flex items-center text-haa-skyblue font-nunito font-bold text-sm group-hover:text-white transition-colors" data-id="element-3845" data-mp="2lkvbhao9ewgk">
                    {isEs ? 'Ver Perfil' : 'View Profile'}
                    <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-3846" data-mp="2uljpw6hkeytz" />
                  </div>
                </div>
              </motion.a>;
        })}
        </div>

        {/* Empty state */}
        {filteredMembers.length === 0 && search.trim() && <div className="text-center py-16" data-id="element-3847" data-mp="1m0ffgqry5w4r">
            <p className="font-bebas text-3xl text-white/30 tracking-wider mb-2" data-id="element-3848" data-mp="1lz4cuua8muf8">
              {isEs ? 'Sin Resultados' : 'No Results'}
            </p>
            <p className="font-nunito text-white/40 text-base" data-id="element-3849" data-mp="1c16o85oguvm3">
              {isEs ? 'Intenta con otra búsqueda' : 'Try a different search term'}
            </p>
          </div>}

        {/* Mobile Back Button */}
        <div className="mt-16 sm:hidden" data-id="element-3850" data-mp="1334hralyrarf">
          <a href="#" className="flex items-center justify-center gap-2 w-full bg-white/10 text-white font-nunito font-bold px-6 py-4 rounded-xl hover:bg-white/20 transition-colors" data-id="element-3851" data-mp="y9t7t0luudva">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3852" data-mp="od6o4f59lsuu" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </div>
      </main>
    </div>;
}